import { usePageState } from "@/navigation/NavigationProvider";
import { useEffect, useState } from "react";
import axios from "axios";
import { Search, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import BoardWorkspace from "@/components/BoardWorkspace";
import ProjectQuickStatusEditor from "@/components/ProjectQuickStatusEditor";
import { useAuth } from "@/auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

export default function ClosedProjectsPage() {
  const { canAccessBilling } = useAuth();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = usePageState("search", "");
  const [reopenTarget, setReopenTarget] = useState(null);

  const load = async (term = "") => {
    const response = await axios.get(`${API}/closed-projects`, { params: { search: term } });
    setRows(response.data.rows);
  };

  useEffect(() => {
    load(search);
  }, [search]);

  return (
    <BoardWorkspace
      eyebrow="Project archive"
      headingTestId="closed-projects-heading"
      title="Closed Projects"
      subtitle="Historical company projects retained for reference."
    >
      <section className="closed-projects-toolbar" data-testid="closed-projects-toolbar">
        <Search size={16} aria-hidden="true" />
        <input
          data-testid="closed-projects-search"
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          placeholder="Search project number, name, customer"
          value={search}
        />
        <span data-testid="closed-projects-count">{rows.length} projects</span>
      </section>
      <section className="board-table-panel closed-projects-table-panel" data-testid="closed-projects-page">
        <div className="board-table-scroll">
          <table className="operations-data-table" data-testid="closed-projects-table">
            <thead>
              <tr>
                <th>Project Number</th>
                <th>Project</th>
                <th>Customer</th>
                <th>Supervisor</th>
                <th>Closed Status</th>
                {canAccessBilling && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((project) => (
                <tr data-testid={`closed-project-${project.id}`} key={project.id}>
                  <td><Link to={`/projects/${project.id}`}>{project.project_code}</Link></td>
                  <td>{project.name}</td>
                  <td>{project.client}</td>
                  <td>{project.supervisor}</td>
                  <td>Closed</td>
                  {canAccessBilling && (
                    <td style={{ textAlign: "right" }}>
                      <button
                        type="button"
                        data-testid={`closed-project-reopen-${project.id}`}
                        onClick={() => setReopenTarget(project)}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 4,
                          background: "white", color: "#0f172a",
                          border: "1px solid #cbd5e1", borderRadius: 6,
                          padding: "0.25rem 0.6rem", cursor: "pointer",
                          fontSize: "0.75rem", fontWeight: 600,
                        }}
                      >
                        <RotateCcw size={12} /> Reopen
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {reopenTarget && (
        <ProjectQuickStatusEditor
          canAccessBilling={canAccessBilling}
          project={reopenTarget}
          onClose={() => setReopenTarget(null)}
          onSaved={async () => {
            await load(search);
          }}
        />
      )}
    </BoardWorkspace>
  );
}

